import React from 'react';
import { useForm } from '@inertiajs/react';
import MainLayout from '../layouts/MainLayout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { FiUser, FiKey, FiShield } from 'react-icons/fi';

export default function Profile({ user }) {
  const { data, setData, patch, processing, errors, reset } = useForm({
    email: user.email || '',
    password: '',
    password_confirmation: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    patch('/facility_owner/profile', {
      onSuccess: () => {
        reset('password', 'password_confirmation');
      },
    });
  };

  return (
    <MainLayout user={user}>
      <div className="py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Profile Settings</h1>
              <p className="mt-1 text-gray-500">Manage your account information and security settings</p>
            </div>
            <Button variant="outline" className="mt-4 md:mt-0" asChild>
              <a href="/facility_owner/dashboard">Back to Dashboard</a>
            </Button>
          </div>

          <Tabs defaultValue="account" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="account" className="flex items-center">
                <FiUser className="h-4 w-4 mr-2" />
                Account
              </TabsTrigger>
              <TabsTrigger value="security" className="flex items-center">
                <FiKey className="h-4 w-4 mr-2" />
                Security
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex items-center">
                <FiShield className="h-4 w-4 mr-2" />
                Notifications
              </TabsTrigger>
            </TabsList>

            <TabsContent value="account">
              <Card>
                <CardHeader>
                  <CardTitle>Account Information</CardTitle>
                  <CardDescription>
                    Update your account details and email preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        type="email"
                        value={data.email}
                        onChange={(e) => setData('email', e.target.value)}
                        className="mt-1"
                      />
                      {errors.email && (
                        <p className="text-sm text-red-500 mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="role">Account Type</Label>
                      <Input
                        id="role"
                        value={user.role === 'facility_owner' ? 'Facility Owner' : user.role}
                        disabled
                        className="mt-1 bg-gray-50"
                      />
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-between border-t px-6 py-4">
                  <Button variant="outline" onClick={() => reset()}>Cancel</Button>
                  <Button onClick={handleSubmit} disabled={processing}>Save Changes</Button>
                </CardFooter>
              </Card>
            </TabsContent>

            <TabsContent value="security">
              <Card>
                <CardHeader>
                  <CardTitle>Security Settings</CardTitle>
                  <CardDescription>
                    Update your password and security preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="current_password">Current Password</Label>
                      <Input
                        id="current_password"
                        type="password"
                        placeholder="Enter your current password"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label htmlFor="password">New Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={data.password}
                        onChange={(e) => setData('password', e.target.value)}
                        className="mt-1"
                      />
                      {errors.password && (
                        <p className="text-sm text-red-500 mt-1">{errors.password}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="password_confirmation">Confirm New Password</Label>
                      <Input
                        id="password_confirmation"
                        type="password"
                        value={data.password_confirmation}
                        onChange={(e) => setData('password_confirmation', e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  <Alert className="bg-amber-50 border-amber-200">
                    <AlertTitle className="text-amber-800 flex items-center">
                      <FiShield className="h-4 w-4 mr-2" />
                      Password Requirements
                    </AlertTitle>
                    <AlertDescription className="text-amber-700">
                      <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                        <li>At least 8 characters long</li>
                        <li>Include at least one uppercase letter</li>
                        <li>Include at least one number</li>
                        <li>Include at least one special character</li>
                      </ul>
                    </AlertDescription>
                  </Alert>
                </CardContent>
                <CardFooter className="flex justify-between border-t px-6 py-4">
                  <Button variant="outline" onClick={() => reset('password', 'password_confirmation')}>
                    Cancel
                  </Button>
                  <Button onClick={handleSubmit} disabled={processing}>
                    Update Password
                  </Button>
                </CardFooter>
              </Card>
            </TabsContent>

            <TabsContent value="notifications">
              <Card>
                <CardHeader>
                  <CardTitle>Notification Preferences</CardTitle>
                  <CardDescription>
                    Control how and when you receive notifications
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <h3 className="text-sm font-medium">New Lead Notifications</h3>
                        <p className="text-sm text-gray-500">
                          Receive notifications when you get new leads
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="lead_notifications"
                          className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          defaultChecked
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <h3 className="text-sm font-medium">Tour Booking Notifications</h3>
                        <p className="text-sm text-gray-500">
                          Receive notifications when a tour is booked
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="tour_notifications"
                          className="h-4 w-4 text-blue-600 rounded border-gray-300"
                          defaultChecked
                        />
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-b">
                      <div>
                        <h3 className="text-sm font-medium">Marketing Updates</h3>
                        <p className="text-sm text-gray-500">
                          Receive updates about new features and promotions
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="marketing_notifications"
                          className="h-4 w-4 text-blue-600 rounded border-gray-300"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end border-t px-6 py-4">
                  <Button>Save Preferences</Button>
                </CardFooter>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </MainLayout>
  );
}
